import { alpha, Box, Grid, Typography, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEntitiesFromSearch } from 'api/search'
import { useUserItems } from 'api/aws/users/useUserItems'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { Attachments } from 'types'
import { sortCommentsByTimeCreated } from 'utils/sortOperators'
import { useDeleteAttachment } from '../../api/aws/comments/useDeleteAttachment'
import { useDeleteCommentAndAttachments } from '../../api/aws/comments/useDeleteCommentAndAttachments'
import { useHandlePostComment } from '../../api/aws/comments/useHandlePostComment'
import { useUniversalNotify } from '../../api/aws/notifications/useUniversalNotify'
import { useUploadNoteAttachments } from '../../api/aws/s3/useUploadNoteAttachments'
import { useTask } from '../../api/aws/tasks/useTask'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useSlackNotification } from '../../api/slack/useSlackNotification'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import DetailsLoading from '../Loading/DetailsLoading'
import TextEditor from '../TextEditor/TextEditor'
import CommentList from './CommentList'
import PlaceholderComment from './PlaceholderComment'
import SubtaskSelector from './SubtaskSelector'
import { extractMentionIdsFromHtml } from './utils/formatHtml'

interface TaskMessagesProps {
  expanded?: boolean
  context?: string
  subtasks: any[]
  isCommentOpen: boolean
  setIsCommentOpen: React.Dispatch<React.SetStateAction<boolean>>
}
// WHY IS COMMENTS BY SK CALLING A SEPARATE QUERY FOR EACH SK
const TaskMessages: React.FC<TaskMessagesProps> = ({
  expanded,
  context,
  subtasks,
  isCommentOpen,
  setIsCommentOpen,
}) => {
  const { PK, SK } = useParams()
  const location = useLocation()
  const _theme = useTheme()
  const queryClient = useQueryClient()
  const { deals, organizations, commentsTable } = useEntitiesFromSearch()
  const { task, isTaskLoading, isTaskError } = useTask(PK, SK)
  const deal = useMemo(() => {
    if (!task || isTaskLoading) return null
    return deals?.find(d => d?.deal_uuid === task?.dealId || d?.deal_uuid === task?.deal_uuid) ?? null
  }, [task, isTaskLoading])
  const organization = useMemo(() => {
    if (!task || isTaskLoading) return null
    return organizations?.find(o => o?.PK === task?.PK) ?? null
  }, [task, isTaskLoading])
  const { activeUser } = useActiveUser()
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  // const { user: currentUser } = useUsersById(activeUser?.user?.id)
  // const { data: commentsByOrgPK, isLoading: isCommentsByPKLoading } = useQueryComments(PK)
  const all = useMemo(() => {
    if (!commentsTable) return []
    return commentsTable ?? []
  }, [commentsTable])
  const taskComments = useMemo(() => {
    if (!all) return []
    return all?.filter(c => c?.PK === SK)
  }, [PK, SK, all])
  const subtaskSKs = subtasks.map(st => st.SK)
  const subtaskComments = all?.filter(c => subtaskSKs?.includes(c?.PK))
  const formattedSubtasks = useMemo(() => {
    if (!subtasks || !subtasks?.length) return []
    return subtasks?.map(st => {
      const count = subtaskComments?.filter(stc => stc?.PK === st?.SK)?.length ?? 0
      return { ...st, commentCount: count }
    })
  }, [subtasks])

  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedSubtask, setSelectedSubtask] = useState<any>(null)
  const subtaskSK = useMemo(() => selectedSubtask?.SK, [selectedSubtask])
  const comments = useMemo(() => {
    const comms = selectedSubtask ? subtaskComments?.filter(stc => stc?.PK === selectedSubtask?.SK) ?? [] : taskComments
    return comms ? sortCommentsByTimeCreated(comms) : []
  }, [selectedSubtask, taskComments, subtaskComments])
  const { handlePostComment } = useHandlePostComment()
  const { notify } = useUniversalNotify()
  const { mutate: sendSlackNotification } = useSlackNotification()
  const { mutate: deleteCommentAndAttachments, isPending } = useDeleteCommentAndAttachments()
  const { mutateAsync: uploadAttachments } = useUploadNoteAttachments()
  const { showSnackbar } = useSnackbarContext()
  const { handleDeleteAttachment } = useDeleteAttachment()
  // const subtaskSKs = subtasks.map(st => st.SK)
  const [newNoteContent, setNewNoteContent] = useState('')
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedFiles, setSelectedFiles] = useState([])
  const [isCreatingNote, setIsCreatingNote] = useState(false)
  const [isFrameOpen, setIsFrameOpen] = useState(false)
  const [selectedEmbedUrl, setSelectedEmbedUrl] = useState<string | null>(null)
  useEffect(() => {
    const taskParam = searchParams.get('task')
    if (taskParam && subtasks?.length > 0) {
      const match = subtasks.find(st => st.SK === taskParam)
      if (match) setSelectedSubtask(match)
    }
  }, [searchParams, subtasks])

  const handleSubtaskSelect = (subtask: any) => {
    if (subtask) {
      setSelectedSubtask(subtask)
      setSearchParams({ task: subtask.SK })
    } else {
      setSelectedSubtask(null)
      setSearchParams({})
    }
  }
  const handleCreateAndNotify = async () => {
    if (!newNoteContent || /^<p>(<br>)?<\/p>$/.test(newNoteContent)) return

    setIsCreatingNote(true)
    try {
      // 1) Build the final HTML (swap pasted blob URLs → S3 URLs)
      let uploaded: Attachments[] = []
      let contentsHtml = newNoteContent

      if (selectedFiles.length > 0) {
        const rawFiles = selectedFiles.map(f => f.file)
        uploaded = await uploadAttachments({ files: rawFiles, orgId: task?.orgId })

        const blobToFinal = new Map<string, string>()
        selectedFiles.forEach((sf, i) => {
          const blob = sf.previewUrl || sf.url
          const final = uploaded[i]?.url
          if (blob && final) blobToFinal.set(blob, final)
        })
        blobToFinal.forEach((finalUrl, blobUrl) => {
          contentsHtml = contentsHtml.split(blobUrl).join(finalUrl)
        })
      }

      // 2) Snapshot everything you’ll need BEFORE any mutations that may re-render
      const currentLocation = location.pathname.split('/')[1] ?? null
      const pk = subtaskSK || task?.SK
      const title = selectedSubtask?.name || task?.name || ''
      const snapshotUsers = [...selectedUsers] // defensive copy
      const snapshotTagIds = snapshotUsers.map(u => u.user_uuid || u.id)
      const fallbackIds = snapshotTagIds.length ? snapshotTagIds : extractMentionIdsFromHtml(contentsHtml)
      const recipients = snapshotUsers.length ? snapshotUsers : fallbackIds.map(id => ({ id } as any))

      // 3) Build the comment payload (final HTML!)
      const commentData = {
        PK: pk,
        orgId: task?.orgId || null,
        title,
        attachments: uploaded,
        owner: activeUser?.user ? [activeUser.user.id] : [],
        taggedUsers: snapshotUsers,
        tagIds: fallbackIds, // <-- keep these so we can audit
        createdAt: new Date().toISOString(),
        context: selectedSubtask ? 'subtask' : 'task',
        contents: contentsHtml,
        identifier: selectedSubtask ? 'Subtask' : 'Task',
        name: activeUser?.user?.fullName || 'Unknown User',
        isDigital: false,
        isCreative: false,
        isWebsite: false,
        queryKey: ['commentsBySk', pk],
      }

      // 4) Build the notification payload using FINAL HTML (sanitize inside hook)
      const shouldNotify = recipients.length > 0
      const notification = shouldNotify
        ? {
            users: recipients, // may be [{ id: 'U…' }]
            tagIds: fallbackIds,
            type: 'mention',
            severity: 'success',
            title: 'Mention',
            origin: currentLocation,
            org_uuid: task?.orgId ?? null,
            itemId: pk,
            message: `You've been tagged in a task message`,
            board: currentLocation ?? 'teamtasks',
            boardItemName: title,
            boardId: pk,
            slackHeaderMessage: 'You were tagged in a comment!',
            slackMessageHtml: contentsHtml, // final HTML; hook will plaintext it
            slackDirectLink: location.pathname + location.search,
            sendSlack: true,
            sendApp: true,
          }
        : null

      // 5) Fire both operations together (robust & efficient)
      const ops: Promise<any>[] = [handlePostComment(commentData)]
      if (notification) ops.push(notify({ data: notification }))

      const results = await Promise.allSettled(ops)
      const notifyResult = results[1]
      if (notifyResult && notifyResult.status === 'rejected') {
        console.warn('Notify failed:', notifyResult.reason)
        // (optional) showSnackbar('Failed to send notifications', 'warning')
      }

      // 6) Clear local editor state
      setNewNoteContent('')
      setSelectedUsers([])
      setSelectedFiles([])
    } catch (err) {
      console.error('❌ Failed to create comment', err)
      showSnackbar('Failed to create comment', 'error')
    } finally {
      setIsCreatingNote(false)
    }
  }

  const handleDeleteNote = async (note: any) => {
    if (!note?.PK || !note?.SK) return

    const key = ['commentsTable']

    // optimistic update
    const prev = queryClient.getQueryData<any[]>(key) ?? []
    const next = prev.filter(c => !(c?.PK === note.PK && c?.SK === note.SK))
    queryClient.setQueryData(key, next)

    try {
      await deleteCommentAndAttachments({ note })
      // (optional) also invalidate to refetch fresh list for this PK
      // queryClient.invalidateQueries({ queryKey: key });
    } catch (err) {
      // rollback
      queryClient.setQueryData(key, prev)
      showSnackbar('Failed to delete comment', 'error')
    }
  }

  const onDeleteAttachment = (commentId: string, attachmentIndex: number) => {
    handleDeleteAttachment(comments, commentId, attachmentIndex)
  }

  const openFrame = (url: string) => {
    setSelectedEmbedUrl(url)
    setIsFrameOpen(true)
  }

  const closeFrame = () => {
    setIsFrameOpen(false)
    setSelectedEmbedUrl(null)
  }
  const editorHeight = !expanded ? '45vh' : '5vh'
  if (isTaskLoading) return <DetailsLoading />
  if (isTaskError || !task) return <Typography>Error loading task</Typography>

  return (
    <Grid container pt={1} sx={{ display: 'flex' }}>
      <Grid item xs={12} sx={{ p: 2 }}>
        <TextEditor
          organization={organization ?? null}
          deal={deal ?? null}
          onUpload={e => {
            const files = Array.from(e.target.files || []).map(file => ({
              id: file.name + Date.now(),
              name: file.name,
              mimeType: file.type,
              previewUrl: URL.createObjectURL(file),
              file,
            }))
            setSelectedFiles(prev => [...prev, ...files])
          }}
          onRemoveFile={i => setSelectedFiles(prev => prev.filter((_, index) => index !== i))}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          selectedTask={selectedSubtask ?? task}
          onContentChange={setNewNoteContent}
          onSave={handleCreateAndNotify}
          newNoteContent={newNoteContent}
          onMentionsChange={setSelectedUsers}
          // height={!expanded ? '45vh' : '30vh'}
          height={editorHeight}
        />
        {/* </Box> */}
      </Grid>

      <Grid
        item
        xs={12}
        sx={{
          maxHeight: !expanded ? '80vh' : '65vh',
          overflowY: 'hidden',
          p: 1,
        }}
      >
        {context !== 'teamtasks' && (
          <Box py={2}>
            <SubtaskSelector
              subtasks={formattedSubtasks}
              onSubtaskSelected={handleSubtaskSelect}
              selectedSubtask={selectedSubtask}
              // commentCounts={subtaskCommentCounts}
            />
          </Box>
        )}

        {comments?.length > 0 ? (
          <CommentList
            notes={comments}
            isDeleting={isPending}
            users={users}
            openFrame={openFrame}
            onDeleteNote={handleDeleteNote}
            onDeleteAttachment={onDeleteAttachment}
            expanded={expanded}
            context={context}
            isCommentOpen={isCommentOpen}
            setIsCommentOpen={setIsCommentOpen}
          />
        ) : (
          <PlaceholderComment expanded={expanded} />
        )}
      </Grid>

      {isFrameOpen && selectedEmbedUrl && (
        <Box
          onClick={closeFrame}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: alpha(_theme.palette.text.primary, 0.8),
            zIndex: 1300,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box sx={{ width: '80%', height: '80%', position: 'relative' }}>
            <iframe src={selectedEmbedUrl} style={{ width: '100%', height: '100%' }} title="File Preview" />
          </Box>
        </Box>
      )}
    </Grid>
  )
}

export default TaskMessages
